import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Briefcase, Building2, CheckCircle2, ChevronRight, FileText, Home, Info, Mail,
  MapPin, Megaphone, Newspaper, Package, ScrollText, ShieldCheck, type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useI18n } from '@/i18n';
import { usePages } from '@/lib/queries';
import type { PageItem } from '@/lib/types';
import { LOCALES } from '@/lib/types';
import { PageHeader } from '@/components/PageHeader';
import { LocaleChips } from './Articles';
import { Chip } from '@/components/ui/Chip';
import { Skeleton } from '@/components/ui/Feedback';

/**
 * PageMeta covers the site's fixed pages that had no editable metadata before —
 * marketing's original complaint (§3.2). Routes are seeded, not user-creatable.
 *
 * The screen groups the six pages the brief names as primary (home, services,
 * offices, about, news, contacts) apart from the supporting ones, gives each a
 * page-specific icon, and colours every tile by how filled it is — so a marketer
 * reads "what still needs SEO" from the grid at a glance, not row by row.
 *
 * PageEditor lives in its own module so TipTap stays out of this route's chunk.
 */
const PAGE_ICONS: Record<string, LucideIcon> = {
  '/': Home,
  '/services': Package,
  '/offices': Building2,
  '/about': Info,
  '/news': Newspaper,
  '/contact': Mail,
  '/track': MapPin,
  '/careers': Briefcase,
  '/media': Megaphone,
  '/privacy': ShieldCheck,
  '/terms': ScrollText,
};

// The brief's list, in the brief's order (§1). Everything else is supporting.
const MAIN_ROUTES = ['/', '/services', '/offices', '/about', '/news', '/contact'];

type PageStatus = 'full' | 'partial' | 'empty';

function pageStatus(page: PageItem): PageStatus {
  if (page.isEmpty) return 'empty';
  return LOCALES.every((l) => page.completeness[l] === 'full') ? 'full' : 'partial';
}

export function Pages() {
  const { t } = useI18n();
  const { data, isLoading } = usePages();

  const items = data?.items ?? [];

  const { main, more, filled, total } = useMemo(() => {
    const order = (route: string) => {
      const i = MAIN_ROUTES.indexOf(route);
      return i === -1 ? MAIN_ROUTES.length : i;
    };
    const main = items.filter((p) => MAIN_ROUTES.includes(p.route)).sort((a, b) => order(a.route) - order(b.route));
    const more = items.filter((p) => !MAIN_ROUTES.includes(p.route));
    const filled = items.filter((p) => pageStatus(p) === 'full').length;
    return { main, more, filled, total: items.length };
  }, [items]);

  return (
    <>
      <PageHeader
        title={t.pages.title}
        subtitle={t.pages.subtitle}
        action={!isLoading && total > 0 ? <CompletionMeter filled={filled} total={total} label={t.pages.summary(filled, total)} /> : undefined}
      />

      <div className="mx-auto flex w-full max-w-content flex-col gap-8 px-6 pb-12">
        {isLoading ? (
          <GridSkeleton />
        ) : (
          <>
            <PageGroup title={t.pages.groupMain} hint={t.pages.groupMainHint} pages={main} />
            {more.length > 0 && <PageGroup title={t.pages.groupMore} hint={t.pages.groupMoreHint} pages={more} />}
          </>
        )}
      </div>
    </>
  );
}

function CompletionMeter({ filled, total, label }: { filled: number; total: number; label: string }) {
  const pct = total === 0 ? 0 : Math.round((filled / total) * 100);
  return (
    <div className="hidden items-center gap-3 sm:flex" title={label}>
      <span className="text-ui text-muted">{label}</span>
      <div className="h-1.5 w-24 overflow-hidden rounded-chip bg-line" aria-hidden>
        <div
          className="h-full rounded-chip bg-brand-500 transition-[width] duration-slow ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="font-mono text-code font-medium tabular-nums text-strong">
        {filled}/{total}
      </span>
    </div>
  );
}

function PageGroup({ title, hint, pages }: { title: string; hint: string; pages: PageItem[] }) {
  const { t } = useI18n();
  if (pages.length === 0) return null;
  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-col gap-0.5">
        <h2 className="text-eyebrow uppercase tracking-[0.08em] text-faint">{title}</h2>
        <p className="text-ui text-muted">{hint}</p>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {pages.map((page) => (
          <PageCard key={page.route} page={page} name={t.pages.routeNames[page.route] ?? page.route} />
        ))}
      </div>
    </section>
  );
}

const TILE: Record<PageStatus, string> = {
  full: 'bg-grass-400/12 text-grass-500 ring-1 ring-inset ring-grass-400/25 dark:text-grass-400',
  partial: 'bg-amber-400/12 text-amber-600 ring-1 ring-inset ring-amber-400/25 dark:text-amber-400',
  empty: 'bg-raised text-faint ring-1 ring-inset ring-line',
};

function StatusChip({ status }: { status: PageStatus }) {
  const { t } = useI18n();
  if (status === 'full') {
    return (
      <Chip tone="grass" icon={<CheckCircle2 aria-hidden className="h-3 w-3" strokeWidth={2.2} />}>
        {t.pages.filled}
      </Chip>
    );
  }
  if (status === 'partial') return <Chip tone="amber">{t.pages.partial}</Chip>;
  return <Chip tone="neutral">{t.pages.empty}</Chip>;
}

function PageCard({ page, name }: { page: PageItem; name: string }) {
  const { t } = useI18n();
  const status = pageStatus(page);
  const Icon = PAGE_ICONS[page.route] ?? FileText;

  return (
    <Link
      to={`/pages/${encodeURIComponent(page.route)}`}
      aria-label={`${name} — ${t.pages.openHint}`}
      className="card card-hover group flex flex-col gap-3.5 p-4"
    >
      <div className="flex items-start gap-3">
        <span className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-control', TILE[status])}>
          <Icon aria-hidden className="h-[1.125rem] w-[1.125rem]" strokeWidth={1.85} />
        </span>
        <span className="min-w-0 flex-1 pt-0.5">
          <span className="block truncate text-body font-semibold text-strong">{name}</span>
          <span className="mt-0.5 block truncate font-mono text-code text-faint">{page.route}</span>
        </span>
        <ChevronRight
          aria-hidden
          className="mt-1 h-4 w-4 shrink-0 -translate-x-1 text-faint opacity-0 transition-ui duration-quick ease-out group-hover:translate-x-0 group-hover:opacity-100"
          strokeWidth={2}
        />
      </div>

      <div className="flex items-center justify-between gap-2 border-t border-line pt-3">
        <StatusChip status={status} />
        <LocaleChips completeness={page.completeness} />
      </div>
    </Link>
  );
}

function GridSkeleton() {
  return (
    <section className="flex flex-col gap-3">
      <Skeleton className="h-4 w-40" />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 6 }, (_, i) => (
          <Skeleton key={i} className="h-[7.5rem] w-full rounded-card" />
        ))}
      </div>
    </section>
  );
}
